import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { NavigateFunction } from "react-router-dom";
import { getCardType } from "@/utils/functions";
import useCybersourceService from "@/hooks/useCybersourceService";
import moment from "moment";
import { paymentCard } from "@/redux/ducks/user";
import { ReducersTypes } from "@/redux/reducers/Models";
import { PaymentCardAction } from "@/redux/sagas/payment/card";

const creditCardSchema = z.object({
  number: z.string().refine(val => getCardType(val) && val?.length >= 18, "Número inválido"),
  name: z.string().refine(val => val.length > 1 && /\D/.test(val ?? ""), "Nome inválido"),
  expiration: z.string().refine(
    (val) => {
      if (!/^\d{2}\/\d{4}$/.test(val)) {
        return false;
      }
      const [month] = val.split('/');
      const monthNum = parseInt(month, 10);
      
      if (monthNum < 1 || monthNum > 12) {
        return false;
      }
      
      const expiration = moment(val, "MM/YYYY", true);
      return expiration.isValid() && moment().diff(expiration, 'M') < 0;
    },
    (val) => {
      if (!/^\d{2}\/\d{4}$/.test(val)) {
        return { message: "Por favor, insira o ano de vencimento com 4 dígitos" };
      }
      
      const [month, year] = val.split('/');
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);
      const currentYear = moment().year();
      
      if (monthNum < 1 || monthNum > 12) {
        if (yearNum < currentYear) {
          return { message: "Mês e ano de vencimento inválidos. Insira um mês de vencimento válido e um ano de vencimento futuro." };
        }
        return { message: "Mês de vencimento inválido." };
      }
      
      const expiration = moment(val, "MM/YYYY", true);
      if (!expiration.isValid() || moment().diff(expiration, 'M') >= 0) {
        return { message: "Ano de vencimento inválido: o cartão informado está expirado" };
      }
      
      return { message: "Ano de vencimento inválido: o cartão informado está expirado" };
    }
  ),
  document: z.string().regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "CPF inválido"),
});

type FormData = z.infer<typeof creditCardSchema>;

export default function useCheckoutCardForm({ navigate }: { navigate: NavigateFunction }) {
  const dispatch = useDispatch();
  const { getSessionId } = useCybersourceService();
  const user = useSelector((state: ReducersTypes) => state.userReducer);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(creditCardSchema),
    mode: "onChange",
  });

  const handlePayment = useCallback((success: boolean) => {
    setIsLoading(false);

    if (!success) {
      setError(true);
      return;
    }

    navigate('/checkout/finished');
  }, [navigate]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (isLoading) return;

      setIsLoading(true);
      setError(false);

      const card = {
        number: data.number,
        name: data.name,
        expiration: data.expiration,
        document: data.document,
      };

      const visitorId = getSessionId()!;

      const payload: PaymentCardAction['payload'] = {
        plate: user?.plate ?? "",
        card,
        address: {
          zipcode: user?.address?.zipcode ?? "",
          streetName: user?.address?.streetName ?? "",
          neighborhood: user?.address?.neighborhood ?? "",
          addressStreet: user?.address?.streetName ?? "",
          number: user?.address?.number ?? "",
          complement: user?.address?.complement ?? "",
          city: user?.address?.city ?? "",
          state: user?.address?.state ?? "",
          stateCode: user?.address?.stateCode ?? "",
        },
        visitorId,
        // @ts-expect-error
        debts: user?.debts ?? [],
        cart: user?.cart ?? [],
        requestId: user?.requestId ?? "",
        callback: handlePayment,
      };

      dispatch(paymentCard(payload));
    },
    [isLoading, dispatch, handlePayment, getSessionId, user]
  );

  return {
    isLoading,
    isDisabled: !isValid || isLoading,
    error,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
  };
} 